import type { MetricKey, TruthMetricBlock, TruthResponse, TruthRow } from '@nono/contract';
import { useMutation, useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { api, queryClient } from '@/lib/api';
import { useFilters } from '@/lib/filters';
import { useViewMessages } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { FounderScorecard } from './FounderScorecard';

/*
 * Sannheten — headline numbers on top, ONE person table underneath.
 *
 * Each row carries `countsIn` (which metric denominators it belongs to), so
 * clicking a tile filters the table to exactly the rows that number is made
 * of: the table proves the number instead of merely accompanying it.
 */

type Tone = 'good' | 'warn' | 'bad' | 'muted';

const nb = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });
const nb1 = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const MESSAGES = {
  nb: {
    // Locale used for the weekday/date/time formatters below. The NUMBER
    // formatters stay nb-NO in both languages (NOK amounts).
    dateLocale: 'nb-NO',

    loading: 'Regner ut sannheten…',
    noData: 'Ingen data',
    title: 'Sannheten',
    sub: 'Pengene inn, møtene ut, og hvert menneske bak tallene. Klikk et tall for å se akkurat de radene det er regnet av.',
    dataAsOf: (when: string) => `Data per ${when}`,
    scanIncomplete: 'Siste scan ble ikke fullført — tallene er varslet som ufullstendige',
    noScanYet: 'Ingen scan kjørt ennå',
    scanning: 'Skanner…',
    refresh: 'Oppdater',

    // trend + tile chrome
    noPrevPeriod: 'ingen forrige periode',
    upFromZero: '▲ fra 0',
    unchanged: 'uendret',
    tileHint: 'Vis radene bak dette tallet',

    // hovedtallene
    spend: 'Penger brukt',
    spendSub: (days: number) => `på ${days} dager`,
    bookings: 'Bookinger inn',
    bookingsSub: (perDay: string, goal: number) =>
      `${perDay} per dag (mål ${goal}) · booket i perioden, timen kan være senere`,
    meetings: 'Møter i kalenderen',
    meetingsSub: (ownCalendar: number, followUps: number) =>
      `møtetid i perioden · ${ownCalendar} via egen kalender, resten booket i Momence${followUps ? ` · ${followUps} er oppfølgingstimer` : ''}`,
    cost: 'Kost per møte',
    costSub: (spend: string, meetings: number) => `${spend} kr ÷ ${meetings} møter`,
    showUp: 'Oppmøte',
    showUpSub: (num: number, den: number, immature: number) =>
      `${num} av ${den} avgjorte${immature ? ` · ${immature} ikke avgjort ennå (i dag/fremover)` : ''}`,
    closing: 'Closing',
    closingSub: (num: number, den: number) => `${num} av ${den} som møtte til prøvetime ble medlem`,
    growth: 'Vekst',
    growthSub: (active: number, added: number, withoutTrial: number, lost: number) =>
      `${active} aktive nå · +${added} nye (${withoutTrial} uten prøvetime i perioden) · −${lost} utløpt`,
    growthUnavailable: (days: number) =>
      `Historisk netto er utilgjengelig mens den nye hendelsesloggen modnes (${days}/28 hele dager).`,
    growthMeasuredSub: (active: number, start: number, end: number, days: number) =>
      `${active} aktive nå · ${start} → ${end} operative aktive over ${days} hele dager · detaljene nye/sluttet kan ikke tidsfestes sikkert`,
    cancellations: 'Kansellering',
    cancellationsSub: (num: number, den: number) => `${num} av ${den} avgjorte timer`,

    // filterlinja
    periodPrefix: 'Periode:',
    periodSuffix: (days: number) => `(${days} dager) — endres med datovelgeren øverst`,
    searchPlaceholder: 'Søk navn eller telefon…',
    searchLabel: 'Søk',
    showing: 'Viser',
    rowCount: (n: number) => `— ${n} rader`,
    showAll: 'Vis alle',
    filterLabel: {
      bookinger: 'bookingene som kom inn i perioden',
      moter: 'møtene som sto i kalenderen i perioden',
      oppmote: 'de avgjorte møtene (oppmøte)',
      closing: 'de modne prøvetimene (closing)',
      vekst: 'de som ble medlem',
      kansellering: 'de avgjorte timene (kansellering)',
    } satisfies Record<MetricKey, string>,

    // persontabellen
    colName: 'Navn',
    colBooked: 'Booket',
    colSlot: 'Møtetid',
    colShowUp: 'Oppmøte',
    colMember: 'Ble medlem',
    colChannel: 'Kanal',
    bookedOwnTitle: 'Da kunden faktisk booket, fra vår egen kalender',
    bookedMomenceTitle:
      'Da vertskapet registrerte timen i Momence – den ekte bookingtiden finnes ikke der',
    ownCalendar: 'Egen kalender',
    emptyFiltered: (what: string) => `Ingen rader i ${what} i denne perioden.`,
    emptySearch: (q: string) => `Ingen rader${q ? ` for «${q}»` : ''} i denne perioden.`,
    statusLabel: {
      mott: 'Møtt',
      'ikke-mott': 'Ikke møtt',
      venter: 'Venter på innkryssing',
      kommer: 'Kommer',
      avbestilt: 'Avbestilt',
      'ikke-i-momence': 'Ikke i Momence',
    } satisfies Record<TruthRow['status'], string>,
    memberLabel: {
      medlem: 'Ble medlem',
      ikke: 'Ikke medlem',
      'var-medlem': 'Medlem fra før',
    } satisfies Record<NonNullable<TruthRow['member']>, string>,

    // «Hva teller hva?»
    explainerSummary: 'Hva teller hva?',
    expQuestionsT: 'To ulike spørsmål, to tall.',
    expQuestionsB:
      '«Bookinger inn» = møter som ble BOOKET i perioden, uansett når timen er (booker noen i dag en time neste tirsdag, teller det i dag). «Møter i kalenderen» = møter som hadde TIDEN sin i perioden. Velger du én enkelt dag ser du derfor gjerne bookinger inn uten møter, eller omvendt. Tabellen viser begge slags rader, og kolonnene «Booket» og «Møtetid» sier hvilken som er hvilken.',
    expChannelsT: 'Alle kanaler teller',
    expChannelsB:
      "— egen widget, ring-meg og møter vertskapet booker i Momence. Bookingdatoen for de Momence-bookede hentes fra salget. Avbestilte teller ikke som møter; de har eget tall. Momence' eget bookingtall for perioden:",
    expCostT: 'Kost per møte',
    expCostB:
      '— all annonsebruk i perioden delt på møtene i kalenderen. Vi tilskriver annonsene alle møter, også de vertskapet booker på telefon.',
    expShowUpT: 'Oppmøte',
    expShowUpB:
      '— av møter til og med i går: andelen krysset inn i Momence. Dagens møter venter til i morgen (innkryssingen henger etter). En glemt knapp teller som ikke møtt.',
    expClosingT: 'Closing',
    expClosingB:
      '— av dem som faktisk møtte til sin første prøvetime i perioden: andelen som ble medlem. Ingen ventetid regnes inn: folk bestemmer seg på eller rett etter prøvetimen, så «ikke medlem» er et svar, ikke et «ennå». Folk som var medlem fra før er ikke med i regnestykket.',
    expGrowthT: 'Vekst',
    expGrowthB:
      '— netto kommer fra statuskontroller som starter etter midnatt og lukker dagen som nettopp ble ferdig. 29 sammenhengende kontroller gir 28 hele dagsintervaller. Momence gir ikke sikre overgangstidspunkter, så «nye», «sluttet», «tilbake», «fryst» og «tint» vises ikke som kalenderdagstall.',
    expBookedT: 'Booket-datoen',
    expBookedB1:
      'er tidspunktet kunden faktisk booket, hentet fra vår egen kalender. For møter som bare finnes i Momence (booket på telefon) vises i stedet dagen vertskapet registrerte timen der, merket med',
    expBookedB2: '– den ekte bookingtiden finnes ikke i Momence.',
    expMissingT: 'Ikke i Momence',
    expMissingB:
      'i tabellen = booket hos oss, men ingen tilsvarende avtale i Momence. En flyttet time regnes fortsatt som samme booking (inntil tre uker fra opprinnelig tid), så dette er ekte manglende registreringer.',
  },
  en: {
    dateLocale: 'en-GB',

    loading: 'Calculating the truth…',
    noData: 'No data',
    title: 'The Truth',
    sub: 'Money in, meetings out, and every person behind the numbers. Click a number to see exactly the rows it is calculated from.',
    dataAsOf: (when: string) => `Data as of ${when}`,
    scanIncomplete: 'The latest scan did not complete — figures are flagged as incomplete',
    noScanYet: 'No scan has run yet',
    scanning: 'Scanning…',
    refresh: 'Refresh',

    noPrevPeriod: 'no previous period',
    upFromZero: '▲ from 0',
    unchanged: 'unchanged',
    tileHint: 'Show the rows behind this number',

    spend: 'Spend',
    spendSub: (days: number) => `over ${days} days`,
    bookings: 'Bookings in',
    bookingsSub: (perDay: string, goal: number) =>
      `${perDay} per day (target ${goal}) · booked in the period, the session may be later`,
    meetings: 'Meetings on the calendar',
    meetingsSub: (ownCalendar: number, followUps: number) =>
      `meeting time in the period · ${ownCalendar} via our own calendar, the rest booked in Momence${followUps ? ` · ${followUps} are follow-up sessions` : ''}`,
    cost: 'Cost per meeting',
    costSub: (spend: string, meetings: number) => `${spend} kr ÷ ${meetings} meetings`,
    showUp: 'Show-up',
    showUpSub: (num: number, den: number, immature: number) =>
      `${num} of ${den} settled${immature ? ` · ${immature} not settled yet (today/upcoming)` : ''}`,
    closing: 'Closing',
    closingSub: (num: number, den: number) =>
      `${num} of ${den} who attended a trial session joined`,
    growth: 'Growth',
    growthSub: (active: number, added: number, withoutTrial: number, lost: number) =>
      `${active} active now · +${added} new (${withoutTrial} without a trial session in the period) · −${lost} expired`,
    growthUnavailable: (days: number) =>
      `Historical net growth is unavailable while the new event ledger matures (${days}/28 complete days).`,
    growthMeasuredSub: (active: number, start: number, end: number, days: number) =>
      `${active} active now · ${start} → ${end} operating active across ${days} complete days · joined/left details cannot be timed reliably`,
    cancellations: 'Cancellations',
    cancellationsSub: (num: number, den: number) => `${num} of ${den} settled sessions`,

    periodPrefix: 'Period:',
    periodSuffix: (days: number) => `(${days} days) — change it with the date picker at the top`,
    searchPlaceholder: 'Search name or phone…',
    searchLabel: 'Search',
    showing: 'Showing',
    rowCount: (n: number) => `— ${n} rows`,
    showAll: 'Show all',
    filterLabel: {
      bookinger: 'the bookings that came in during the period',
      moter: 'the meetings that were on the calendar in the period',
      oppmote: 'the settled meetings (show-up)',
      closing: 'the mature trial sessions (closing)',
      vekst: 'those who joined',
      kansellering: 'the settled sessions (cancellations)',
    } satisfies Record<MetricKey, string>,

    colName: 'Name',
    colBooked: 'Booked',
    colSlot: 'Meeting time',
    colShowUp: 'Show-up',
    colMember: 'Joined',
    colChannel: 'Channel',
    bookedOwnTitle: 'When the customer actually booked, from our own calendar',
    bookedMomenceTitle:
      'When the hosts registered the session in Momence – the real booking time does not exist there',
    ownCalendar: 'Own calendar',
    emptyFiltered: (what: string) => `No rows in ${what} in this period.`,
    emptySearch: (q: string) => `No rows${q ? ` for “${q}”` : ''} in this period.`,
    statusLabel: {
      mott: 'Attended',
      'ikke-mott': 'No-show',
      venter: 'Awaiting check-in',
      kommer: 'Upcoming',
      avbestilt: 'Cancelled',
      'ikke-i-momence': 'Not in Momence',
    } satisfies Record<TruthRow['status'], string>,
    memberLabel: {
      medlem: 'Joined',
      ikke: 'Did not join',
      'var-medlem': 'Already a member',
    } satisfies Record<NonNullable<TruthRow['member']>, string>,

    explainerSummary: 'What counts as what?',
    expQuestionsT: 'Two different questions, two numbers.',
    expQuestionsB:
      '“Bookings in” = meetings that were BOOKED in the period, no matter when the session itself is (if someone books next Tuesday’s session today, it counts today). “Meetings on the calendar” = meetings whose TIME fell inside the period. So if you pick a single day you will often see bookings in without meetings, or the other way around. The table shows both kinds of rows, and the “Booked” and “Meeting time” columns tell you which is which.',
    expChannelsT: 'Every channel counts',
    expChannelsB:
      "— our own widget, call-me requests and meetings the hosts book in Momence. For the Momence-booked ones the booking date comes from the sale. Cancellations do not count as meetings; they have their own number. Momence' own booking count for the period:",
    expCostT: 'Cost per meeting',
    expCostB:
      '— all ad spend in the period divided by the meetings on the calendar. We credit the ads with every meeting, including the ones the hosts book over the phone.',
    expShowUpT: 'Show-up',
    expShowUpB:
      '— of meetings up to and including yesterday: the share checked in in Momence. Today’s meetings wait until tomorrow (check-in lags behind). A forgotten button counts as a no-show.',
    expClosingT: 'Closing',
    expClosingB:
      '— of those who actually attended their first trial session in the period: the share who became members. No waiting time is counted in: people decide during or right after the trial session, so “did not join” is an answer, not a “not yet”. People who were already members are left out of the calculation.',
    expGrowthT: 'Growth',
    expGrowthB:
      '— net comes from status checkpoints that start after midnight and close the day that just ended. 29 consecutive checkpoints provide 28 complete daily intervals. Momence does not expose reliable transition timestamps, so joined, left, returned, frozen and unfrozen are not shown as calendar-day counts.',
    expBookedT: 'The Booked date',
    expBookedB1:
      'is the moment the customer actually booked, taken from our own calendar. For meetings that only exist in Momence (booked over the phone) we instead show the day the hosts registered the session there, marked with',
    expBookedB2: '– the real booking time does not exist in Momence.',
    expMissingT: 'Not in Momence',
    expMissingB:
      'in the table = booked with us, but no matching appointment in Momence. A moved session still counts as the same booking (up to three weeks from the original time), so these are genuine missing registrations.',
  },
};

/** «2026-08-14» → «to. 14.08» */
function shortDay(iso: string, locale: string): string {
  if (!iso) return '–';
  const d = new Date(`${iso}T12:00:00Z`);
  const wd = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(d);
  return `${wd} ${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
}

const TONE_TEXT: Record<Tone, string> = {
  good: 'text-good-fg',
  warn: 'text-warn-fg',
  bad: 'text-bad-fg',
  muted: 'text-fg',
};

const STATUS_CHIP_CLS: Record<TruthRow['status'], string> = {
  mott: 'bg-good-bg text-good-fg',
  'ikke-mott': 'bg-bad-bg text-bad-fg',
  venter: 'bg-warn-bg text-warn-fg',
  kommer: 'bg-surface-2 text-fg-muted',
  avbestilt: 'bg-bad-bg text-bad-fg',
  'ikke-i-momence': 'bg-warn-bg text-warn-fg',
};

const MEMBER_CHIP_CLS: Record<NonNullable<TruthRow['member']>, string> = {
  medlem: 'bg-good-bg text-good-fg',
  ikke: 'bg-bad-bg text-bad-fg',
  'var-medlem': 'text-fg-subtle',
};

function Trend({
  block,
  higherIsBetter,
  unit,
}: {
  block: TruthMetricBlock;
  higherIsBetter: boolean;
  unit: 'pp' | '%' | 'abs';
}) {
  const m = useViewMessages(MESSAGES);
  const { value, prevValue: prev } = block;
  if (value == null || prev == null) {
    return <span className="text-micro text-fg-subtle">{m.noPrevPeriod}</span>;
  }
  if (unit === '%' && prev <= 0) {
    return (
      <span
        className={cn('text-micro font-semibold', higherIsBetter ? 'text-good-fg' : 'text-bad-fg')}
      >
        {value > 0 ? m.upFromZero : m.unchanged}
      </span>
    );
  }
  const diff = value - prev;
  if (Math.abs(diff) < 0.05)
    return <span className="text-micro text-fg-subtle">{m.unchanged}</span>;
  const up = diff > 0;
  const label =
    unit === 'pp'
      ? `${nb1.format(Math.abs(diff))} pp`
      : unit === '%'
        ? `${nb.format(Math.abs((100 * diff) / prev))} %`
        : nb.format(Math.abs(diff));
  return (
    <span
      className={cn(
        'text-micro font-semibold',
        up === higherIsBetter ? 'text-good-fg' : 'text-bad-fg',
      )}
    >
      {up ? '▲' : '▼'} {label}
    </span>
  );
}

function Tile({
  label,
  value,
  tone = 'muted',
  sub,
  block,
  higherIsBetter,
  unit,
  active,
  onClick,
}: {
  label: string;
  value: string;
  tone?: Tone;
  sub?: string;
  block?: TruthMetricBlock;
  higherIsBetter?: boolean;
  unit?: 'pp' | '%' | 'abs';
  active?: boolean;
  onClick?: () => void;
}) {
  const m = useViewMessages(MESSAGES);
  const clickable = Boolean(onClick);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      aria-pressed={clickable ? Boolean(active) : undefined}
      title={clickable ? m.tileHint : undefined}
      className={cn(
        'min-w-0 rounded-lg border bg-surface p-3 text-left shadow-card transition-fast sm:p-4',
        active ? 'border-brand ring-1 ring-brand' : 'border-border',
        clickable ? 'cursor-pointer hover:border-brand/60' : 'cursor-default',
      )}
    >
      <div className="truncate text-label text-fg-muted">{label}</div>
      <div className={cn('mt-1.5 truncate text-2xl font-bold tabular', TONE_TEXT[tone])}>
        {value}
      </div>
      <div className="mt-1 flex items-center gap-2">
        {block && unit ? (
          <Trend block={block} higherIsBetter={higherIsBetter ?? true} unit={unit} />
        ) : null}
      </div>
      {sub ? <div className="mt-1 text-micro leading-snug text-fg-subtle">{sub}</div> : null}
    </button>
  );
}

export function SannhetenPage() {
  const m = useViewMessages(MESSAGES);
  // The date range is the app's GLOBAL filter (the Shopify-style picker in the
  // top bar) — same control every other page uses, so a period chosen here
  // follows you to Oversikt/Ads and back.
  const { from, to } = useFilters();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<MetricKey | null>(null);

  const query = useQuery({
    queryKey: ['truth', from, to],
    queryFn: () => api.truth({ from, to }),
  });
  const growthQuery = useQuery({
    queryKey: ['growth-summary', from, to],
    queryFn: () => api.growthSummary({ from, to }),
  });
  const refreshMut = useMutation({
    mutationFn: () => api.truthRefresh(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['truth'] });
      void queryClient.invalidateQueries({ queryKey: ['growth-summary'] });
    },
  });

  const d: TruthResponse | undefined = query.data;
  const rows = useMemo(() => {
    if (!d) return [];
    const needle = q.trim().toLowerCase();
    return d.people.filter(
      (r) =>
        (!filter || r.countsIn.includes(filter)) &&
        (!needle || r.name.toLowerCase().includes(needle) || (r.phone ?? '').includes(needle)),
    );
  }, [d, q, filter]);

  if (query.isLoading) return <LoadingState label={m.loading} />;
  if (query.isError) return <ErrorState onRetry={() => void query.refetch()} />;
  if (!d) return <EmptyState title={m.noData} />;

  const lenDays = Math.round((Date.parse(d.to) - Date.parse(d.from)) / 86_400_000) + 1;
  const perDay = d.moter.perDay ?? 0;
  const moterTone: Tone = perDay >= d.goalBookingsPerDay ? 'good' : perDay >= 2 ? 'warn' : 'bad';
  const kost = d.kostPerMote.value;
  const kostTone: Tone =
    kost == null
      ? 'muted'
      : Math.round(kost) <= 250
        ? 'good'
        : Math.round(kost) <= 450
          ? 'warn'
          : 'bad';
  const opp = d.oppmote.value;
  const oppTone: Tone =
    opp == null ? 'muted' : Math.round(opp) >= 80 ? 'good' : Math.round(opp) >= 60 ? 'warn' : 'bad';
  const clo = d.closing.value;
  const cloTone: Tone =
    clo == null ? 'muted' : Math.round(clo) >= 50 ? 'good' : Math.round(clo) >= 30 ? 'warn' : 'bad';
  const trustedFlow = growthQuery.data?.membershipFlow;
  const trustedFlowMeasured = trustedFlow?.status === 'measured';

  // Clicking a tile clears the search: the banner asserts "these are the rows
  // behind the number", which is only true when nothing else narrows them.
  const toggle = (k: MetricKey) => {
    setQ('');
    setFilter((cur) => (cur === k ? null : k));
  };

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{m.title}</h1>
          <p className="text-sm text-fg-subtle">{m.sub}</p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-micro',
              d.truthRefresh.latestRun?.status && d.truthRefresh.latestRun.status !== 'ok'
                ? 'text-warn-fg'
                : 'text-fg-subtle',
            )}
          >
            {d.truthRefresh.latestRun?.status && d.truthRefresh.latestRun.status !== 'ok'
              ? m.scanIncomplete
              : d.scannedAt
                ? m.dataAsOf(
                    new Date(d.scannedAt).toLocaleString(m.dateLocale, {
                      timeZone: 'Europe/Oslo',
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  )
                : m.noScanYet}
          </span>
          <button
            type="button"
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending}
            className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-surface-2 disabled:opacity-50"
          >
            <RefreshCw
              className={cn('size-3.5', refreshMut.isPending && 'animate-spin')}
              aria-hidden
            />
            {refreshMut.isPending ? m.scanning : m.refresh}
          </button>
        </div>
      </div>

      <FounderScorecard
        data={growthQuery.data}
        loading={growthQuery.isLoading}
        error={growthQuery.isError}
        onRetry={() => void growthQuery.refetch()}
      />

      {/* Hovedtallene */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Tile
          label={m.spend}
          value={`${nb.format(d.spend.value ?? 0)} kr`}
          sub={m.spendSub(lenDays)}
          block={d.spend}
          higherIsBetter={false}
          unit="%"
        />
        <Tile
          label={m.bookings}
          value={nb.format(d.bookingerInn.value ?? 0)}
          tone={
            (d.bookingerInn.perDay ?? 0) >= d.goalBookingsPerDay
              ? 'good'
              : (d.bookingerInn.perDay ?? 0) >= 2
                ? 'warn'
                : 'bad'
          }
          sub={m.bookingsSub(nb1.format(d.bookingerInn.perDay ?? 0), d.goalBookingsPerDay)}
          block={d.bookingerInn}
          higherIsBetter
          unit="abs"
          active={filter === 'bookinger'}
          onClick={() => toggle('bookinger')}
        />
        <Tile
          label={m.meetings}
          value={nb.format(d.moter.value ?? 0)}
          tone={moterTone}
          sub={m.meetingsSub(d.moterEgenKalender, d.moterOppfolging)}
          block={d.moter}
          higherIsBetter
          unit="abs"
          active={filter === 'moter'}
          onClick={() => toggle('moter')}
        />
        <Tile
          label={m.cost}
          value={kost == null ? '–' : `${nb.format(kost)} kr`}
          tone={kostTone}
          sub={m.costSub(nb.format(d.kostPerMote.numerator ?? 0), d.kostPerMote.denominator ?? 0)}
          block={d.kostPerMote}
          higherIsBetter={false}
          unit="%"
        />
        <Tile
          label={m.showUp}
          value={opp == null ? '–' : `${nb.format(opp)} %`}
          tone={oppTone}
          sub={m.showUpSub(
            d.oppmote.numerator ?? 0,
            d.oppmote.denominator ?? 0,
            d.oppmote.immature ?? 0,
          )}
          block={d.oppmote}
          higherIsBetter
          unit="pp"
          active={filter === 'oppmote'}
          onClick={() => toggle('oppmote')}
        />
        <Tile
          label={m.closing}
          value={clo == null ? '–' : `${nb.format(clo)} %`}
          tone={cloTone}
          sub={m.closingSub(d.closing.numerator ?? 0, d.closing.denominator ?? 0)}
          block={d.closing}
          higherIsBetter
          unit="pp"
          active={filter === 'closing'}
          onClick={() => toggle('closing')}
        />
      </div>

      {/* Sekundært: vekst + kansellering */}
      <div className="grid grid-cols-2 gap-3">
        <Tile
          label={m.growth}
          value={
            trustedFlowMeasured && trustedFlow?.net != null
              ? `${trustedFlow.net >= 0 ? '+' : ''}${nb.format(trustedFlow.net)}`
              : '–'
          }
          tone={
            trustedFlowMeasured && trustedFlow?.net != null
              ? trustedFlow.net > 0
                ? 'good'
                : trustedFlow.net === 0
                  ? 'warn'
                  : 'bad'
              : 'muted'
          }
          sub={
            trustedFlowMeasured
              ? m.growthMeasuredSub(
                  growthQuery.data?.membership.activeNow ?? 0,
                  trustedFlow?.startActive ?? 0,
                  trustedFlow?.endActive ?? 0,
                  Math.max(0, (trustedFlow?.checkpointCount ?? 1) - 1),
                )
              : m.growthUnavailable(trustedFlow?.trackedCompleteDays ?? 0)
          }
        />
        <Tile
          label={m.cancellations}
          value={d.kansellering.value == null ? '–' : `${nb.format(d.kansellering.value)} %`}
          tone={
            d.kansellering.value == null
              ? 'muted'
              : d.kansellering.value <= 10
                ? 'good'
                : d.kansellering.value <= 20
                  ? 'warn'
                  : 'bad'
          }
          sub={m.cancellationsSub(d.kansellering.numerator ?? 0, d.kansellering.denominator ?? 0)}
          block={d.kansellering}
          higherIsBetter={false}
          unit="pp"
          active={filter === 'kansellering'}
          onClick={() => toggle('kansellering')}
        />
      </div>

      {/* Søk — datoperioden styres av velgeren i topplinja */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-fg-subtle">
          {m.periodPrefix}{' '}
          <strong className="text-fg-muted">
            {shortDay(d.from, m.dateLocale)} – {shortDay(d.to, m.dateLocale)}
          </strong>{' '}
          {m.periodSuffix(lenDays)}
        </span>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={m.searchPlaceholder}
          className="ml-auto w-52 rounded-md border border-border bg-surface px-3 py-1.5 text-xs"
          aria-label={m.searchLabel}
        />
      </div>

      {filter ? (
        <div className="flex items-center gap-2 rounded-md border border-brand/40 bg-brand-tint px-3 py-2 text-xs">
          <span className="text-brand">
            {m.showing} <strong>{m.filterLabel[filter]}</strong> {m.rowCount(rows.length)}
          </span>
          <button
            type="button"
            onClick={() => setFilter(null)}
            className="ml-auto font-semibold text-brand hover:underline"
          >
            {m.showAll}
          </button>
        </div>
      ) : null}

      {/* Persontabellen */}
      <div className="overflow-x-auto rounded-lg border border-border bg-surface shadow-card">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-border bg-surface-2 text-label text-fg-muted">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">{m.colName}</th>
              <th className="px-3 py-2 text-left font-semibold">{m.colBooked}</th>
              <th className="px-3 py-2 text-left font-semibold">{m.colSlot}</th>
              <th className="px-3 py-2 text-left font-semibold">{m.colShowUp}</th>
              <th className="px-3 py-2 text-left font-semibold">{m.colMember}</th>
              <th className="px-3 py-2 text-left font-semibold">{m.colChannel}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.key} className="hover:bg-surface-2">
                <td className="px-3 py-2">
                  <div className="font-medium">{r.name}</div>
                  {r.phone ? <div className="text-micro text-fg-subtle">{r.phone}</div> : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-fg-muted">
                  {r.bookedAt ? (
                    <span
                      title={r.bookedSource === 'egen' ? m.bookedOwnTitle : m.bookedMomenceTitle}
                    >
                      {shortDay(r.bookedAt, m.dateLocale)}
                      {r.bookedSource === 'momence' ? (
                        <span className="text-fg-subtle"> *</span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="text-fg-subtle">–</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {shortDay(r.slotAt, m.dateLocale)}{' '}
                  <span className="text-fg-subtle">{r.slotTime}</span>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-micro font-semibold',
                      STATUS_CHIP_CLS[r.status],
                    )}
                  >
                    {m.statusLabel[r.status]}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {r.member ? (
                    <span className="inline-flex flex-col gap-0.5">
                      <span
                        className={cn(
                          'inline-flex w-fit whitespace-nowrap rounded-full px-2 py-0.5 text-micro font-semibold',
                          MEMBER_CHIP_CLS[r.member],
                        )}
                      >
                        {m.memberLabel[r.member]}
                      </span>
                      {r.member !== 'ikke' && r.memberSince ? (
                        <span className="text-micro text-fg-subtle">
                          {shortDay(r.memberSince, m.dateLocale)}
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="text-micro text-fg-subtle">–</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-micro text-fg-subtle">
                  {r.source === 'egen' ? m.ownCalendar : 'Momence'}
                </td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-fg-subtle">
                  {filter ? m.emptyFiltered(m.filterLabel[filter]) : m.emptySearch(q)}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <details className="rounded-lg border border-border bg-surface p-3 text-xs text-fg-subtle">
        <summary className="cursor-pointer font-semibold text-fg-muted">
          {m.explainerSummary}
        </summary>
        <ul className="mt-2 flex flex-col gap-1.5 leading-relaxed">
          <li>
            <strong>{m.expQuestionsT}</strong> {m.expQuestionsB}
          </li>
          <li>
            <strong>{m.expChannelsT}</strong> {m.expChannelsB} <strong>{d.momenceBookings}</strong>.
          </li>
          <li>
            <strong>{m.expCostT}</strong> {m.expCostB}
          </li>
          <li>
            <strong>{m.expShowUpT}</strong> {m.expShowUpB}
          </li>
          <li>
            <strong>{m.expClosingT}</strong> {m.expClosingB}
          </li>
          <li>
            <strong>{m.expGrowthT}</strong> {m.expGrowthB}
          </li>
          <li>
            <strong>{m.expBookedT}</strong> {m.expBookedB1} <strong>*</strong> {m.expBookedB2}
          </li>
          <li>
            <strong>{m.expMissingT}</strong> {m.expMissingB}
          </li>
        </ul>
      </details>
    </div>
  );
}
